import {
    type RestoreEquipmentTypeRequest,
    type RestoreEquipmentTypeResponse,
    RestoreEquipmentTypeResponseSchema,
    type RetireEquipmentTypeRequest,
    type RetireEquipmentTypeResponse,
    RetireEquipmentTypeResponseSchema,
    type RetireMoveCounts,
} from '@rock/types';
import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * Retire a catalog type, or with `targetTypeId` also move its equipment, models, coverage rules, recall
 * classifications and guide starters onto that type. `dryRun` only reports what the type is used by.
 */
export async function retireEquipmentType(request: RetireEquipmentTypeRequest): Promise<RetireEquipmentTypeResponse> {
    // The server moves everything in one call (up to 540 s); the client default of 70 s would give up first.
    const fn = httpsCallable<RetireEquipmentTypeRequest, RetireEquipmentTypeResponse>(
        getFunctions(),
        'retireEquipmentType',
        { timeout: 540_000 },
    );
    const result = await fn(request);
    return RetireEquipmentTypeResponseSchema.parse(result.data);
}

/** Bring a retired type back, reopening the guide starters its retire dismissed in every home. */
export async function restoreEquipmentType(
    request: RestoreEquipmentTypeRequest,
): Promise<RestoreEquipmentTypeResponse> {
    const fn = httpsCallable<RestoreEquipmentTypeRequest, RestoreEquipmentTypeResponse>(
        getFunctions(),
        'restoreEquipmentType',
        { timeout: 540_000 },
    );
    const result = await fn(request);
    return RestoreEquipmentTypeResponseSchema.parse(result.data);
}

/** One-line operator summary of what a retire moved. */
export function formatMovedSummary(moved: RetireMoveCounts | undefined): string {
    if (!moved) return '';
    const parts = [
        countLabel(moved.equipment, 'equipment'),
        countLabel(moved.models, 'model', 'models'),
        countLabel(moved.documents, 'document', 'documents'),
        countLabel(moved.recalls, 'recall', 'recalls'),
        countLabel(moved.starters, 'guide starter', 'guide starters'),
    ].filter((part) => !!part);
    return parts.length > 0 ? `Moved ${parts.join(', ')}` : '';
}

function countLabel(count: number, one: string, many = one): string {
    if (!count) return '';
    return `${count} ${count === 1 ? one : many}`;
}
